/**
 * Command surface only — local UI state (e.g. CSV drag). No useEffects keyed off tab/context/timer
 * that trigger network calls; rate limits and greetings are handled in useIdwiz.
 */
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import FindingsBrief from "../FindingsBrief.jsx";
import ExecutiveDashboard from "../ExecutiveDashboard.jsx";
import WorkspaceCommandBar from "../workspace/WorkspaceCommandBar.jsx";
import WorkspaceTabShell from "../workspace/WorkspaceTabShell.jsx";
import MonitoringTabContent from "../workspace/MonitoringTabContent.jsx";
import GapAnalysisTabContent from "../workspace/GapAnalysisTabContent.jsx";
import WorkspaceChatDock, {
  chatDockSummaryLabel,
  defaultChatDockExpandedForTab,
} from "../workspace/WorkspaceChatDock.jsx";
import DriftReport from "../DriftReport.jsx";
import { ACTIVE_WORKFLOW_CSV_IMPORT } from "../../hooks/useIdwiz.js";
import ArchitectsNote from "./ArchitectsNote.jsx";
import DiscoveryOptionGrid from "./DiscoveryOptionGrid.jsx";
import RateLimitDisplay from "./RateLimitDisplay.jsx";
import SswsTenantStrip from "./SswsTenantStrip.jsx";
import { isOinSearchField } from "./DynamicForm.jsx";
import OnboardingWizardPanel from "./OnboardingWizardPanel.jsx";
import GroupRulesPanel from "./GroupRulesPanel.jsx";
import AutomationProgressBar from "./AutomationProgressBar.jsx";
import CurrentTask from "./CurrentTask.jsx";
import { WarningBanner } from "./WarningBanner.jsx";
import { retrieveToken } from "../../services/tokenVault.js";
import { runOktaOrgAuditAsync } from "../../utils/oktaOrgAuditAsync.js";
import {
  getCommandWindowChatPlaceholder,
  getOpenInAdminConsoleLabel,
  getWorkspaceBreadcrumbLabel,
  isAuth0Project,
  isOktaProject,
  isEntraProject,
  isOryProject,
  isOrySelfHostedConnected,
  isOrySelfHostedProject,
} from "../../utils/projectProvider.js";
import OryAttestationGate from "../ory/OryAttestationGate.jsx";
import { isWriteIntent } from "../../utils/isWriteIntent.mjs";
import {
  finishAssessmentProgress,
  GENERIC_AUDIT_STEP_DEFS,
  ORY_AUDIT_STEP_DEFS,
  startAssessmentProgressTicker,
} from "../../utils/orgAuditProgressSteps.js";

export default function CommandWindow({
  phase,
  thinking,
  commandIntentLabel = null,
  selectedTreePath = "",
  messages = [],
  input = "",
  setInput,
  sendMessage,
  /** Programmatic chat send (same pipeline as typing in the input). Used by audit brief + pivots. */
  submitUserMessage = null,
  applyNextStep,
  currentContext = "",
  conversationMemory = null,
  activeOptionButtons = [],
  canCreate = true,
  canMutateTab = true,
  canApproveExecute = false,
  consultationMode = true,
  onOpenNavigation,
  formActive = false,
  activeFormFields = null,
  taskFormValues = {},
  setTaskFormValue,
  currentTaskTitle = null,
  lastAssistantBrief = "",
  manualRetryPending = false,
  dailyLimitReached = false,
  sendReadyPulse = false,
  inputRef = null,
  handleInputChange = null,
  inputWakeBounce = false,
  onCsvFile = null,
  onboardingWizard = null,
  onOnboardingSelectSource = null,
  onboardingGroupsText = "",
  onOnboardingGroupsTextChange = null,
  onOnboardingContinueStep2 = null,
  onOnboardingSubmitPlan = null,
  onExitOnboardingWizard = null,
  activeTask = null,
  groupRulesView = null,
  approveExecuteReady = false,
  approveDisabledHint = null,
  executionSubmitting = false,
  onTaskExecute = null,
  executionProgress = null,
  refiningAgentResponse = false,
  showCsvButton = false,
  activeWorkflow = null,
  handleFormPrimaryAction = null,
  handleOinCatalogSearchEnter = null,
  /** Same as handleOinCatalogSearchEnter — explicit name for OIN primary + Enter. */
  fetchOinResults = null,
  executeOinSearch = null,
  oinCatalogUi = null,
  oinCatalogSearchBusy = false,
  oinConfigureUi = null,
  oinConfigureSubmitting = false,
  handleOinConfigureFieldChange = null,
  handleOinConfigureSubmit = null,
  handleOinConfigureCancel = null,
  handleOinAddToOkta = null,
  oinAddingAppId = null,
  chatHttp429Active = false,
  rateLimitRetrySeconds = 0,
  /** Second click on “Retry Now” (same gesture as double-click) bypasses local send lock. */
  forceQuotaRetryNow = null,
  dismissSuggestedActionSurface = null,
  warning = null,
  bestPracticeViolation = false,
  onDismissWarning = null,
  auditData = null,
  driftData = null,
  project = null,
  isGreenfield = false,
  setAuditData = null,
  setDriftData = null,
  /** Phase G.1 — persist audit + reload project so architect narrative can resync. */
  syncAfterAssessment = null,
  /** When true, DynamicForm Primary works like inline OIN/SAML (no main Execute gate). Includes generic app-create forms. */
  taskFormUsesInlinePrimary = false,
  /** Optional label from Architect JSON (primaryActionLabel); overrides defaults when set. */
  architectPrimaryActionLabel = null,
}) {
  const navigate = useNavigate();
  const [csvDragOver, setCsvDragOver] = useState(false);
  const [driftDismissed, setDriftDismissed] = useState(false);
  const [workspaceTab, setWorkspaceTab] = useState("dashboard");
  const [oryGapAnalysisEnabled, setOryGapAnalysisEnabled] = useState(false);
  const [snapshotList, setSnapshotList] = useState([]);
  const [selectedSnapshotId, setSelectedSnapshotId] = useState(null);
  const [snapBusy, setSnapBusy] = useState(false);
  const [orgAuditProgressSteps, setOrgAuditProgressSteps] = useState(null);
  const [snapError, setSnapError] = useState("");
  const [trendDetailBusy, setTrendDetailBusy] = useState(false);
  const [trendDetail, setTrendDetail] = useState(null); // { resolved: [], surfaced: [] }
  const [priorSnapshot, setPriorSnapshot] = useState(null);
  const [chatDockExpanded, setChatDockExpanded] = useState(false);
  const [oryAttestationOpen, setOryAttestationOpen] = useState(false);
  const oryAttestationRetryRef = useRef(false);
  const chatDockUserToggledRef = useRef(false);
  const threadEndRef = useRef(null);
  const showActButton = useMemo(
    () => Boolean(input?.trim()) && isWriteIntent(input),
    [input],
  );
  /** Single scroll column for audit brief + chat so tall audits aren’t clipped by nested overflow / scrollIntoView. */
  const mainScrollRef = useRef(null);
  const csvInputRef = useRef(null);
  const csvImportPickerVisible =
    activeWorkflow === ACTIVE_WORKFLOW_CSV_IMPORT && typeof onCsvFile === "function";
  const navAndFormLocked = thinking;
  const actionChipsDisabled = thinking;
  const inputLocked = thinking;
  /** Top-bar idle label — never show default phase "gathering" at rest (agent loop default, not live status). */
  const headerIdleStatus =
    phase === "executing" || executionSubmitting
      ? "Executing"
      : phase === "recommending"
        ? "Recommending"
        : "Ready";

  const messageLabel = (m) => {
    if (m.role === "user") return "You";
    return "idwiz AI";
  };

  // Okta-specific: legacy SSWS tenant strip during non-consultation phases
  const tenantTouchPhase = !consultationMode && isOktaProject(project);

  const wizardActive = Boolean(onboardingWizard);
  /** Chat input is disabled during the guided onboarding wizard (no architect until submit). */
  const chatLocked = inputLocked || wizardActive;
  const visibleChatMessages = useMemo(() => {
    const systemPatterns = [
      "[SYSTEM:",
      "[SYSTEM_ERROR:",
      "[PIVOT:",
      "[FORM_SUBMITTED:",
      "[Selected]",
      "[Selected:",
      "[SELECTED:",
      "[CONTEXT_SWITCH]",
      "[ACK:",
    ];
    return (messages || []).filter((msg) => {
      if (msg.hidden === true) return false;
      if (msg.role === "system") return false;
      if (msg.isExecuting) return false;
      if (msg.role !== "user" && msg.role !== "assistant") return false;
      const content = typeof msg.content === "string" ? msg.content : "";
      if (systemPatterns.some((p) => content.startsWith(p))) return false;
      if (/\[SYSTEM(?:_ERROR)?:/i.test(content)) return false;
      if (msg.role === "assistant") {
        try {
          const parsed = JSON.parse(content);
          if (parsed && typeof parsed === "object" && parsed._ack === true) return false;
          if (
            parsed &&
            typeof parsed === "object" &&
            parsed.message === null &&
            !parsed.optionButtons?.length
          ) {
            return false;
          }
        } catch {
          /* not JSON */
        }
      }
      return true;
    });
  }, [messages]);

  const zenHistory = formActive || wizardActive ? [] : visibleChatMessages.slice(-12);

  const scrollMainToBottom = useCallback(() => {
    const el = mainScrollRef.current;
    if (!el) return;
    requestAnimationFrame(() => {
      el.scrollTo({ top: el.scrollHeight, behavior: "smooth" });
    });
  }, []);

  useEffect(() => {
    setWorkspaceTab("dashboard");
    setDriftDismissed(false);
    chatDockUserToggledRef.current = false;
    setChatDockExpanded(false);
  }, [project?.projectId]);

  useEffect(() => {
    fetch("/api/health", { credentials: "include" })
      .then((r) => r.json())
      .then((d) => setOryGapAnalysisEnabled(Boolean(d?.features?.oryGapAnalysis)))
      .catch(() => setOryGapAnalysisEnabled(false));
  }, []);

  const handleWorkspaceTabChange = useCallback((tab) => {
    setWorkspaceTab(tab);
    if (!chatDockUserToggledRef.current) {
      setChatDockExpanded(defaultChatDockExpandedForTab(tab));
    }
  }, []);

  const handleChatDockToggle = useCallback(() => {
    chatDockUserToggledRef.current = true;
    setChatDockExpanded((v) => !v);
  }, []);

  const refreshSnapshots = useCallback(
    async ({ selectNewest = false } = {}) => {
      const pid = project?.projectId;
      if (!pid) return [];
      setSnapError("");
      setSnapBusy(true);
      try {
        const res = await fetch(`/api/projects/${encodeURIComponent(pid)}/snapshots`, { credentials: "include" });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.ok === false) throw new Error(data.error || "Failed to load snapshots");
        const snaps = Array.isArray(data.snapshots) ? data.snapshots : [];
        setSnapshotList(snaps);
        if (selectNewest && snaps[0]?.snapshotId) {
          setSelectedSnapshotId(String(snaps[0].snapshotId));
        } else if (!selectedSnapshotId && snaps[0]?.snapshotId) {
          setSelectedSnapshotId(String(snaps[0].snapshotId));
        }
        return snaps;
      } catch (e) {
        setSnapError(e?.message || "Failed to load snapshots");
        return [];
      } finally {
        setSnapBusy(false);
      }
    },
    [project?.projectId, selectedSnapshotId],
  );

  useEffect(() => {
    if (!project?.projectId) return;
    void refreshSnapshots();
  }, [project?.projectId, refreshSnapshots]);

  useEffect(() => {
    const pid = project?.projectId;
    const snaps = snapshotList;
    if (!pid || !Array.isArray(snaps) || snaps.length < 2 || !auditData?.findings) {
      setPriorSnapshot(null);
      return;
    }
    const activeId = selectedSnapshotId || snaps[0]?.snapshotId;
    const idx = snaps.findIndex((s) => String(s.snapshotId) === String(activeId));
    const priorMeta = idx >= 0 ? snaps[idx + 1] : snaps[1];
    if (!priorMeta?.snapshotId) {
      setPriorSnapshot(null);
      return;
    }
    let cancelled = false;
    void (async () => {
      try {
        const res = await fetch(
          `/api/projects/${encodeURIComponent(pid)}/snapshots/${encodeURIComponent(String(priorMeta.snapshotId))}`,
          { credentials: "include" },
        );
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (res.ok && data.snapshot) setPriorSnapshot(data.snapshot);
        else setPriorSnapshot(null);
      } catch {
        if (!cancelled) setPriorSnapshot(null);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [project?.projectId, snapshotList, selectedSnapshotId, auditData?.findings]);

  const loadSnapshot = useCallback(async (snapshotId) => {
    const pid = project?.projectId;
    if (!pid || !snapshotId) return;
    setSnapError("");
    setSnapBusy(true);
    try {
      const res = await fetch(
        `/api/projects/${encodeURIComponent(pid)}/snapshots/${encodeURIComponent(String(snapshotId))}`,
        { credentials: "include" },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok || data.ok === false) throw new Error(data.error || "Failed to load snapshot");
      const snap = data.snapshot;
      if (snap && typeof setAuditData === "function") setAuditData(snap);
      if (snap && typeof setDriftData === "function") setDriftData(null);
      setSelectedSnapshotId(String(snapshotId));
    } catch (e) {
      setSnapError(e?.message || "Failed to load snapshot");
    } finally {
      setSnapBusy(false);
    }
  }, [project?.projectId, setAuditData, setDriftData]);

  const computeDiff = (older, newer) => {
    const failSet = (snap) => {
      const f = snap?.findings || {};
      const crit = Array.isArray(f.critical) ? f.critical : [];
      const warn = Array.isArray(f.warnings) ? f.warnings : [];
      const failed = [...crit, ...warn].map((x) => String(x?.ruleId || x?.id || x?.title || "")).filter(Boolean);
      return new Set(failed);
    };
    const passSet = (snap) => {
      const f = snap?.findings || {};
      const healthy = Array.isArray(f.healthy) ? f.healthy : [];
      const passed = healthy.map((x) => String(x?.ruleId || x?.id || x?.title || "")).filter(Boolean);
      return new Set(passed);
    };
    const aFail = failSet(older);
    const bFail = failSet(newer);
    const aPass = passSet(older);
    const bPass = passSet(newer);
    const resolved = [...aFail].filter((id) => bPass.has(id) || !bFail.has(id)).slice(0, 8);
    const surfaced = [...bFail].filter((id) => aPass.has(id) || !aFail.has(id)).slice(0, 8);
    return { resolved, surfaced };
  };

  const loadTrendDetail = useCallback(async () => {
    const pid = project?.projectId;
    const snaps = snapshotList;
    if (!pid || !Array.isArray(snaps) || snaps.length < 2) return;
    const latestId = String(snaps[0].snapshotId);
    const firstId = String(snaps[snaps.length - 1].snapshotId);
    setTrendDetailBusy(true);
    try {
      const [aRes, bRes] = await Promise.all([
        fetch(`/api/projects/${encodeURIComponent(pid)}/snapshots/${encodeURIComponent(firstId)}`, {
          credentials: "include",
        }),
        fetch(`/api/projects/${encodeURIComponent(pid)}/snapshots/${encodeURIComponent(latestId)}`, {
          credentials: "include",
        }),
      ]);
      const a = await aRes.json().catch(() => ({}));
      const b = await bRes.json().catch(() => ({}));
      if (!aRes.ok) throw new Error(a.error || "Failed to load older snapshot");
      if (!bRes.ok) throw new Error(b.error || "Failed to load latest snapshot");
      setTrendDetail(computeDiff(a.snapshot, b.snapshot));
    } catch (e) {
      setTrendDetail({ resolved: [], surfaced: [], error: e?.message || "Failed to compute trends" });
    } finally {
      setTrendDetailBusy(false);
    }
  }, [project?.projectId, snapshotList]);

  useEffect(() => {
    if (workspaceTab !== "monitoring") return;
    void loadTrendDetail();
  }, [workspaceTab, loadTrendDetail]);

  const runNewAssessment = useCallback(async () => {
    const pid = project?.projectId;
    if (!pid) return;

    if (isAuth0Project(project)) {
      setSnapError("");
      setSnapBusy(true);
      const stopProgress = startAssessmentProgressTicker(
        setOrgAuditProgressSteps,
        GENERIC_AUDIT_STEP_DEFS,
      );
      try {
        const res = await fetch("/api/idp/auth0/org-audit", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projectId: pid, trigger: "manual" }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.ok === false) throw new Error(data.error || "Auth0 assessment failed");
        if (typeof syncAfterAssessment === "function") {
          await syncAfterAssessment(data.audit, data.drift || null);
        } else {
          if (data.audit && typeof setAuditData === "function") setAuditData(data.audit);
          if (typeof setDriftData === "function") setDriftData(data.drift || null);
        }
        await refreshSnapshots({ selectNewest: true });
        finishAssessmentProgress(setOrgAuditProgressSteps, GENERIC_AUDIT_STEP_DEFS);
      } catch (e) {
        setSnapError(e?.message || "Auth0 assessment failed");
      } finally {
        stopProgress();
        setSnapBusy(false);
        setOrgAuditProgressSteps(null);
      }
      return;
    }

    if (isOryProject(project)) {
      setSnapError("");
      setSnapBusy(true);
      const stopProgress = startAssessmentProgressTicker(setOrgAuditProgressSteps, ORY_AUDIT_STEP_DEFS);
      try {
        const res = await fetch("/api/idp/ory/org-audit", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projectId: pid, trigger: "manual" }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.ok === false) {
          if (res.status === 428 && isOrySelfHostedProject(project)) {
            oryAttestationRetryRef.current = true;
            setOryAttestationOpen(true);
            return;
          }
          throw new Error(data.error || "Ory assessment failed");
        }
        if (typeof syncAfterAssessment === "function") {
          await syncAfterAssessment(data.audit, data.drift || null);
        } else {
          if (data.audit && typeof setAuditData === "function") setAuditData(data.audit);
          if (typeof setDriftData === "function") setDriftData(data.drift || null);
        }
        await refreshSnapshots({ selectNewest: true });
        finishAssessmentProgress(setOrgAuditProgressSteps, ORY_AUDIT_STEP_DEFS);
      } catch (e) {
        setSnapError(e?.message || "Ory assessment failed");
      } finally {
        stopProgress();
        setSnapBusy(false);
        setOrgAuditProgressSteps(null);
      }
      return;
    }

    if (isEntraProject(project)) {
      setSnapError("");
      setSnapBusy(true);
      const stopProgress = startAssessmentProgressTicker(
        setOrgAuditProgressSteps,
        GENERIC_AUDIT_STEP_DEFS,
      );
      try {
        const res = await fetch("/api/idp/entra/org-audit", {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ projectId: pid, trigger: "manual" }),
        });
        const data = await res.json().catch(() => ({}));
        if (!res.ok || data.ok === false) throw new Error(data.error || "Entra org audit failed");
        if (typeof syncAfterAssessment === "function") {
          await syncAfterAssessment(data.audit, data.drift || null);
        } else {
          if (data.audit && typeof setAuditData === "function") setAuditData(data.audit);
          if (typeof setDriftData === "function") setDriftData(data.drift || null);
        }
        await refreshSnapshots({ selectNewest: true });
        finishAssessmentProgress(setOrgAuditProgressSteps, GENERIC_AUDIT_STEP_DEFS);
      } catch (e) {
        setSnapError(e?.message || "Entra org audit failed");
      } finally {
        stopProgress();
        setSnapBusy(false);
        setOrgAuditProgressSteps(null);
      }
      return;
    }

    const orgUrl = project?.org?.url;
    if (!orgUrl) return;
    const tok = retrieveToken(orgUrl);
    if (!tok) {
      window.alert("Missing SSWS token for this org. Reconnect the org first.");
      return;
    }
    setSnapError("");
    setSnapBusy(true);
    setOrgAuditProgressSteps(null);
    try {
      const data = await runOktaOrgAuditAsync({
        orgUrl,
        apiToken: tok,
        projectId: pid,
        trigger: "manual",
        onProgress: ({ steps, phaseLabel, fanoutCompleted, fanoutTotal }) => {
          if (Array.isArray(steps)) setOrgAuditProgressSteps(steps);
          if (
            typeof fanoutCompleted === "number" &&
            typeof fanoutTotal === "number" &&
            fanoutTotal > 0
          ) {
            setOrgAuditProgressSteps((prev) => {
              const base = Array.isArray(prev) ? prev : steps;
              if (!Array.isArray(base)) return prev;
              return base.map((s) =>
                s.id === "groups"
                  ? {
                      ...s,
                      label: `${phaseLabel || s.label} (${fanoutCompleted} of ${fanoutTotal})`,
                    }
                  : s,
              );
            });
          }
        },
      });
      if (typeof syncAfterAssessment === "function") {
        await syncAfterAssessment(data.audit, data.drift || null);
      } else {
        if (data.audit && typeof setAuditData === "function") setAuditData(data.audit);
        if (typeof setDriftData === "function") setDriftData(data.drift || null);
      }
      await refreshSnapshots({ selectNewest: true });
    } catch (e) {
      const msg =
        e?.code === "ORG_AUDIT_TIMEOUT"
          ? e.message
          : e?.message || "Org audit failed";
      setSnapError(msg);
    } finally {
      setSnapBusy(false);
      setOrgAuditProgressSteps(null);
    }
  }, [project, refreshSnapshots, setAuditData, setDriftData, syncAfterAssessment]);

  const handleOryAttestationComplete = useCallback(() => {
    setOryAttestationOpen(false);
    if (oryAttestationRetryRef.current) {
      oryAttestationRetryRef.current = false;
      void runNewAssessment();
    }
  }, [runNewAssessment]);

  useEffect(() => {
    const pid = project?.projectId;
    if (!pid || !isOrySelfHostedProject(project)) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`/api/projects/${encodeURIComponent(pid)}/ory-attestation`, {
          credentials: "include",
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled) return;
        if (
          data.requires_attestation &&
          (!data.attestation_present || data.attestation_stale) &&
          !oryAttestationRetryRef.current
        ) {
          setOryAttestationOpen(true);
        }
      } catch {
        /* fail-closed: user can open gate via Run assessment (428) */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [project?.projectId, project?.credentialMetadata?.deployment_mode]);

  const handleFindingsAction = useCallback(
    (actionId) => {
      const submit = typeof submitUserMessage === "function" ? submitUserMessage : null;
      if (!submit) return;

      if (typeof actionId === "string" && actionId.startsWith("framework:")) {
        const fw = actionId.slice("framework:".length).trim();
        if (fw) {
          void submit(
            `[PIVOT: Focus on compliance framework "${fw}"]\n\n` +
              `Show only the findings relevant to ${fw}. For each item, cite the matching controls from the finding.frameworks array (do not invent control numbers).`,
          );
        }
        queueMicrotask(() => scrollMainToBottom());
        return;
      }

      const findings = auditData?.findings;
      const crit = Array.isArray(findings?.critical) ? findings.critical : [];
      const warn = Array.isArray(findings?.warnings) ? findings.warnings : [];
      const healthy = Array.isArray(findings?.healthy) ? findings.healthy : [];

      if (actionId === "fix-critical") {
        const criticalList =
          crit.length > 0 ? crit.map((f) => `· ${f.title}: ${f.detail}`).join("\n") : "See audit findings";
        void submit(
          `[PIVOT: Fix critical issues first]\n\nCritical findings to address:\n${criticalList}\n\nStart with the highest impact item. For each one, tell me what you're doing and do it.`,
        );
      } else if (actionId === "full-report") {
        if (project?.projectId) {
          const base = `/project/${encodeURIComponent(project.projectId)}/report`;
          const path = selectedSnapshotId
            ? `${base}/${encodeURIComponent(selectedSnapshotId)}`
            : base;
          navigate(path);
          return;
        }
        void submit(
          `[PIVOT: Summarize the audit]\n\nGive a short executive-style summary of critical and warning findings only (no need to paste every healthy item).`,
        );
      } else if (actionId === "specific-task") {
        void submit(
          `[PIVOT: User has something specific to do — ask what they want to work on next, and offer 3–4 optionButtons for common tasks.]`,
        );
      }
      queueMicrotask(() => scrollMainToBottom());
    },
    [auditData?.findings, submitUserMessage, scrollMainToBottom, project?.projectId, selectedSnapshotId, navigate],
  );

  const oryWorkspace = isOryProject(project);
  const normalizedFindings = Array.isArray(auditData?.assessment?.findings)
    ? auditData.assessment.findings
    : [];
  const oryHasAssessmentView =
    oryWorkspace && normalizedFindings.length > 0 && Boolean(auditData?.findings);

  const orySelfHostedConnected = isOrySelfHostedConnected(project);
  const showWorkspaceTabs =
    Boolean(project?.projectId) &&
    (orySelfHostedConnected ||
      (Boolean(auditData?.findings) && (!isGreenfield || oryHasAssessmentView)));
  const showGapAnalysisTab = oryWorkspace && oryGapAnalysisEnabled && showWorkspaceTabs;
  const workspaceExtraTabs = showGapAnalysisTab
    ? [{ id: "gap-analysis", label: "Gap Analysis" }]
    : [];

  useEffect(() => {
    if (workspaceTab === "gap-analysis" && !showGapAnalysisTab) {
      setWorkspaceTab("dashboard");
    }
  }, [workspaceTab, showGapAnalysisTab]);
  const showDrift = Boolean(driftData?.hasDrift) && !driftDismissed;

  const chatDockExpandedEffective =
    !showWorkspaceTabs || formActive || wizardActive ? true : chatDockExpanded;
  const chatDockSummary = chatDockSummaryLabel({
    snapBusy,
    thinking,
    hasAssessment: Boolean(auditData?.findings),
    headerIdleStatus,
  });

  const workspaceStatusLabel = snapBusy
    ? "Running assessment…"
    : thinking
      ? "idwiz is thinking…"
      : headerIdleStatus;

  useEffect(() => {
    scrollMainToBottom();
  }, [messages, thinking, formActive, wizardActive, scrollMainToBottom]);

  const groupRulesPanelVisible =
    activeTask === "review_group_rules" || (groupRulesView && groupRulesView.status !== "idle");
  const groupRulesLoading = groupRulesView?.status === "loading";
  const showThinkingBanner = thinking && !(activeTask === "review_group_rules" && groupRulesLoading);

  const showCsvExecute = activeTask === "csv_user_import" && !formActive;
  const formUsesInlinePrimaryExecute = formActive && taskFormUsesInlinePrimary;
  const formHasOinSearch =
    Boolean(formActive && Array.isArray(activeFormFields) && activeFormFields.some(isOinSearchField));
  /** OIN catalog task: no suggested-action chips / custom chat input — only CurrentTask + Cancel (Dashboard/TaskForm). */
  const oinSearchTaskLocked = activeTask === "OIN_SEARCH";
  const showSuggestedActionsChrome =
    (formActive || wizardActive || showCsvExecute) && !oinSearchTaskLocked;
  let oinSearchInputTrimmed = "";
  if (formHasOinSearch && Array.isArray(activeFormFields)) {
    for (const f of activeFormFields) {
      if (!isOinSearchField(f)) continue;
      const id = f?.id != null ? String(f.id) : "";
      if (!id) continue;
      oinSearchInputTrimmed = String(taskFormValues[id] ?? "").trim();
      break;
    }
  }
  const runOinSearch =
    typeof executeOinSearch === "function"
      ? executeOinSearch
      : typeof fetchOinResults === "function"
        ? fetchOinResults
        : handleOinCatalogSearchEnter;
  /** Never a generic "Primary action" when the task type is known (TaskForm / DynamicForm). */
  const primaryActionLabelResolved = (() => {
    const fromClaude =
      typeof architectPrimaryActionLabel === "string" && architectPrimaryActionLabel.trim()
        ? architectPrimaryActionLabel.trim()
        : null;

    if (activeTask === "OIN_SEARCH" || formHasOinSearch) {
      if (oinCatalogSearchBusy) return "Searching…";
      if (oinConfigureSubmitting) return "Working…";
      return oinSearchInputTrimmed.length > 0 ? "Search OIN Catalog" : "Search OIN";
    }

    if (
      executionSubmitting &&
      (activeTask === "CUSTOM_SAML" ||
        activeTask === "manual_user_create" ||
        activeTask === "oin_servicenow" ||
        (taskFormUsesInlinePrimary && activeTask !== "OIN_SEARCH" && !formHasOinSearch))
    ) {
      return "Creating…";
    }

    if (fromClaude) return fromClaude;

    if (activeTask === "manual_user_create") return "Create";
    if (activeTask === "oin_servicenow") return "Create";
    if (activeTask === "CUSTOM_SAML") return "Create Application";
    if (
      taskFormUsesInlinePrimary &&
      activeTask !== "manual_user_create" &&
      activeTask !== "oin_servicenow" &&
      activeTask !== "CUSTOM_SAML" &&
      activeTask !== "OIN_SEARCH" &&
      !formHasOinSearch
    ) {
      return "Create Application";
    }
    return "Primary action";
  })();
  /** Allow Primary on inline-execute tasks (SAML, user create, ServiceNow) while the agent is still streaming. */
  const taskFormFieldsDisabled = thinking && !formHasOinSearch && !formUsesInlinePrimaryExecute;
  const showPrimaryExecute =
    typeof onTaskExecute === "function" && !formUsesInlinePrimaryExecute && showCsvExecute;
  const executeLabel = (() => {
    if (executionSubmitting || phase === "executing") return "Creating…";
    if (activeTask === "oin_servicenow" || activeTask === "manual_user_create" || activeTask === "csv_user_import") {
      return "Create";
    }
    return "Execute";
  })();

  const handleCsvDragOver = useCallback((e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "copy";
  }, []);

  const handleCsvDragEnter = useCallback((e) => {
    e.preventDefault();
    if (e.dataTransfer?.types?.includes("Files")) setCsvDragOver(true);
  }, []);

  const handleCsvDragLeave = useCallback((e) => {
    if (e.currentTarget.contains(e.relatedTarget)) return;
    setCsvDragOver(false);
  }, []);

  const handleCsvDrop = useCallback(
    (e) => {
      e.preventDefault();
      setCsvDragOver(false);
      const f = e.dataTransfer?.files?.[0];
      if (f && typeof onCsvFile === "function") onCsvFile(f);
    },
    [onCsvFile],
  );

  const onPickCsvClick = useCallback(() => {
    csvInputRef.current?.click();
  }, []);

  const onCsvInputChange = useCallback(
    (e) => {
      const f = e.target.files?.[0];
      e.target.value = "";
      if (f && typeof onCsvFile === "function") onCsvFile(f);
    },
    [onCsvFile],
  );

  const handleFormSubmit = useCallback(
    (values) => {
      // eslint-disable-next-line no-console -- triage form submit pipeline
      console.log("[CommandWindow] handleFormSubmit called", values);
      if (typeof handleFormPrimaryAction === "function") return handleFormPrimaryAction(values);
    },
    [handleFormPrimaryAction],
  );

  const workspaceBreadcrumbLabel = useMemo(
    () => getWorkspaceBreadcrumbLabel(project, selectedTreePath),
    [project, selectedTreePath],
  );

  return (
    <section
      className="flex min-h-0 flex-1 flex-col"
      style={{ background: "var(--bg-base)", borderLeft: "1px solid var(--border)" }}
    >
      <div
        className="flex shrink-0 items-center justify-between border-b px-4"
        style={{
          height: "32px",
          background: "var(--bg-elevated)",
          borderColor: "var(--border)",
        }}
      >
        <span className="font-mono text-dim truncate text-xs" title={workspaceBreadcrumbLabel}>
          {workspaceBreadcrumbLabel || currentContext}
        </span>
        <div className="flex flex-1 items-center justify-end gap-3">
          {typeof onOpenNavigation === "function" ? (
            <button
              type="button"
              onClick={onOpenNavigation}
              disabled={navAndFormLocked}
              className="rounded border border-white/10 bg-white/[0.04] px-2 py-0.5 text-[10px] text-slate-300 hover:bg-white/[0.08] disabled:opacity-40"
            >
              Nav
            </button>
          ) : null}
          {!showWorkspaceTabs ? (
            thinking ? (
              <span className="text-accent flex items-center gap-2 text-xs">
                <span
                  className="inline-block h-3 w-3 animate-spin rounded-full border-2 border-[var(--border-bright)] border-t-[var(--accent)]"
                  aria-hidden
                />
                idwiz is thinking…
              </span>
            ) : snapBusy ? (
              <span className="text-dim text-xs">Running assessment…</span>
            ) : (
              <span className="text-dim text-xs">{headerIdleStatus}</span>
            )
          ) : null}
        </div>
      </div>

      {showWorkspaceTabs ? (
        <WorkspaceCommandBar
          snapshots={snapshotList}
          selectedSnapshotId={selectedSnapshotId}
          onSelectSnapshot={(id) => void loadSnapshot(id)}
          onRunNewAssessment={() => void runNewAssessment()}
          snapshotsBusy={snapBusy}
          snapshotsError={snapError}
          assessmentProgressSteps={orgAuditProgressSteps}
          statusLabel={workspaceStatusLabel}
          statusBusy={snapBusy || thinking}
        />
      ) : null}

      <div className="flex min-h-0 min-w-0 flex-1 flex-col">
        {showWorkspaceTabs ? (
          <WorkspaceTabShell
            activeTab={workspaceTab}
            onTabChange={handleWorkspaceTabChange}
            extraTabs={workspaceExtraTabs}
          >
            {workspaceTab === "dashboard" ? (
              <ExecutiveDashboard
                auditData={auditData}
                snapshots={snapshotList}
                priorSnapshot={priorSnapshot}
                selectedSnapshotId={selectedSnapshotId}
                driftData={driftData}
                providerId={project?.providerId}
                isGreenfield={Boolean(isGreenfield)}
              />
            ) : null}
            {workspaceTab === "report" ? (
              <FindingsBrief
                findings={auditData.findings}
                summary={auditData.summary}
                onAction={handleFindingsAction}
                snapshots={snapshotList}
                selectedSnapshotId={selectedSnapshotId}
                onSelectSnapshot={(id) => void loadSnapshot(id)}
                onRunNewAssessment={() => void runNewAssessment()}
                snapshotsBusy={snapBusy}
                snapshotsError={snapError}
                assessmentProgressSteps={orgAuditProgressSteps}
                projectId={project?.projectId}
                reportSnapshotId={selectedSnapshotId}
                providerId={project?.providerId}
                project={project}
                normalizedFindings={normalizedFindings}
                isGreenfield={Boolean(isGreenfield)}
                hideWorkspaceControls
              />
            ) : null}
            {workspaceTab === "monitoring" ? (
              <MonitoringTabContent
                projectId={project?.projectId}
                providerId={project?.providerId}
                snapshots={snapshotList}
                auditData={auditData}
                trendDetail={trendDetail}
                trendDetailBusy={trendDetailBusy}
                driftData={driftData}
                priorSnapshot={priorSnapshot}
              />
            ) : null}
            {workspaceTab === "gap-analysis" && showGapAnalysisTab ? (
              <GapAnalysisTabContent
                projectId={project?.projectId}
                snapshotId={selectedSnapshotId}
                auditData={auditData}
                orgName={project?.org?.name}
              />
            ) : null}
          </WorkspaceTabShell>
        ) : null}

        <WorkspaceChatDock
          expanded={chatDockExpandedEffective}
          onToggleExpanded={handleChatDockToggle}
          summaryLabel={chatDockSummary}
          showToggle={showWorkspaceTabs && !formActive && !wizardActive}
          capped={showWorkspaceTabs}
          threadRef={mainScrollRef}
          threadChildren={
            <>
        {showDrift ? (
          <div className="border-b px-4 py-2" style={{ borderColor: "var(--border)" }}>
            <DriftReport
              changes={Array.isArray(driftData?.changes) ? driftData.changes : []}
              onDismiss={() => setDriftDismissed(true)}
              onContinue={() => setDriftDismissed(true)}
              onFixRegressions={() => setDriftDismissed(true)}
            />
          </div>
        ) : null}

        <div className="space-y-3 px-4 py-3">
        {formActive ? (
          <CurrentTask
            currentTaskTitle={currentTaskTitle}
            lastAssistantBrief={lastAssistantBrief}
            activeFormFields={activeFormFields}
            taskFormValues={taskFormValues}
            setTaskFormValue={setTaskFormValue}
            formFieldsDisabled={taskFormFieldsDisabled}
            executeSectionDisabled={navAndFormLocked}
            handleFormPrimaryAction={handleFormSubmit}
            formUsesInlinePrimaryExecute={formUsesInlinePrimaryExecute}
            formHasOinSearch={formHasOinSearch}
            primaryActionLabelResolved={primaryActionLabelResolved}
            primaryDisabled={
              formHasOinSearch
                ? taskFormFieldsDisabled ||
                  executionSubmitting ||
                  oinCatalogSearchBusy ||
                  oinSearchInputTrimmed.length < 3 ||
                  oinConfigureUi?.status === "ready"
                : formUsesInlinePrimaryExecute
                  ? taskFormFieldsDisabled || executionSubmitting || oinCatalogSearchBusy
                  : !formUsesInlinePrimaryExecute ||
                    !approveExecuteReady ||
                    taskFormFieldsDisabled ||
                    executionSubmitting ||
                    oinCatalogSearchBusy
            }
            primaryBusy={
              executionSubmitting ||
              (formHasOinSearch && oinCatalogSearchBusy) ||
              (formHasOinSearch && oinConfigureSubmitting)
            }
            executeOinSearch={runOinSearch}
            oinCatalogUi={oinCatalogUi}
            oinCatalogSearchBusy={oinCatalogSearchBusy}
            oinConfigureUi={oinConfigureUi}
            oinConfigureSubmitting={oinConfigureSubmitting}
            onOinConfigureFieldChange={handleOinConfigureFieldChange}
            onOinConfigureSubmit={handleOinConfigureSubmit}
            onOinConfigureCancel={handleOinConfigureCancel}
            onOinAddToOkta={handleOinAddToOkta}
            oinAddingAppId={oinAddingAppId}
            minOinSearchLength={3}
            dismissSuggestedActionSurface={dismissSuggestedActionSurface}
            showPrimaryExecute={showPrimaryExecute}
            onTaskExecute={onTaskExecute}
            executionSubmitting={executionSubmitting}
            phase={phase}
            approveExecuteReady={approveExecuteReady}
            approveDisabledHint={approveDisabledHint}
            executeLabel={executeLabel}
          />
        ) : showCsvExecute ? (
          <div className="mr-4 rounded-xl border border-[rgb(var(--idaxis-accent-primary-rgb)/0.25)] bg-[rgb(var(--idaxis-accent-primary-rgb)/0.06)] px-4 py-5">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-[rgb(var(--idaxis-accent-primary-rgb)/0.9)]">Current task</p>
            <p className="mt-1 text-base font-semibold text-slate-100">{currentTaskTitle || "Bulk import"}</p>
            {lastAssistantBrief ? (
              <p className="mt-3 whitespace-pre-wrap text-[13px] leading-relaxed text-slate-300">{lastAssistantBrief}</p>
            ) : null}
            {showPrimaryExecute ? (
              <div className="mt-5 border-t border-[rgb(var(--idaxis-accent-primary-rgb)/0.2)] pt-4">
                <button
                  type="button"
                  onClick={() => onTaskExecute()}
                  disabled={
                    navAndFormLocked || executionSubmitting || phase === "executing" || !approveExecuteReady
                  }
                  title={!approveExecuteReady ? approveDisabledHint || undefined : undefined}
                  className="w-full rounded-lg bg-[rgb(var(--idaxis-accent-primary-rgb)/1)] py-2.5 text-sm font-bold text-[var(--idaxis-bg-primary)] shadow-[0_0_20px_rgb(var(--idaxis-accent-primary-rgb)/0.3)] transition hover:shadow-[0_0_28px_rgb(var(--idaxis-accent-primary-rgb)/0.45)] disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {executeLabel}
                </button>
                {approveDisabledHint && !approveExecuteReady ? (
                  <p className="mt-2 text-center text-[10px] text-slate-500">{approveDisabledHint}</p>
                ) : null}
              </div>
            ) : null}
          </div>
        ) : wizardActive ? (
          <OnboardingWizardPanel
            step={onboardingWizard.step}
            answers={onboardingWizard.answers}
            groupsText={onboardingGroupsText}
            onGroupsTextChange={onOnboardingGroupsTextChange}
            onSelectSource={onOnboardingSelectSource}
            onContinueFromStep2={onOnboardingContinueStep2}
            onSubmitPlan={onOnboardingSubmitPlan}
            onExit={onExitOnboardingWizard}
            disabled={thinking}
            thinking={thinking}
          />
        ) : (
          <Fragment>
            {bestPracticeViolation && warning ? (
              <WarningBanner warning={warning} onDismiss={onDismissWarning} />
            ) : null}
            {zenHistory.map((m) =>
              m.isExecuting ? (
                <div
                  key={m.id || m.timestamp}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    padding: "8px 16px",
                    color: "var(--accent)",
                    fontSize: 12,
                    fontFamily: "var(--font-mono, ui-monospace, monospace)",
                  }}
                >
                  <span
                    style={{
                      width: 8,
                      height: 8,
                      borderRadius: "50%",
                      background: "var(--accent)",
                      animation: "pulse 1s infinite",
                    }}
                  />
                  {m.content}
                </div>
              ) : (
              <div
                key={m.id ?? m.timestamp}
                className={`rounded-xl border px-3 py-2.5 text-sm leading-relaxed ${
                  m.role === "user"
                    ? "ml-6 border-white/[0.06] bg-white/[0.03] text-slate-200"
                    : m.kind === "context_notice"
                      ? "mr-4 border-emerald-500/20 bg-emerald-500/[0.06] text-slate-100"
                      : m.kind === "success_card"
                        ? "mr-4 border-emerald-500/35 bg-emerald-500/[0.08] text-slate-100"
                        : m.kind === "error"
                          ? "mr-4 border-rose-500/20 bg-rose-500/[0.06] text-slate-100"
                          : m.kind === "group_rules_audit"
                            ? "mr-4 border-amber-400/25 bg-amber-500/[0.08] text-slate-100"
                            : "mr-4 border-[rgb(var(--idaxis-accent-primary-rgb)/0.15)] bg-[rgb(var(--idaxis-accent-primary-rgb)/0.04)] text-slate-100"
                }`}
              >
                <p
                  className={`text-[10px] font-semibold uppercase tracking-wider ${
                    m.kind === "context_notice"
                      ? "text-emerald-300/90"
                      : m.kind === "success_card"
                        ? "text-emerald-200/95"
                        : m.kind === "error"
                          ? "text-rose-300/90"
                          : m.kind === "group_rules_audit"
                            ? "text-amber-200/90"
                            : "text-[rgb(var(--idaxis-accent-primary-rgb)/0.9)]"
                  }`}
                >
                  {messageLabel(m)}
                </p>
                <p className="mt-1 whitespace-pre-wrap text-[13px]">{m.content}</p>

                {Array.isArray(m.architectureSteps) && m.architectureSteps.length > 0 ? (
                  <div
                    style={{
                      marginTop: 12,
                      borderTop: "1px solid var(--border)",
                      paddingTop: 12,
                    }}
                  >
                    {m.architectureSteps.map((step, i) => {
                      const kind = String(step?.kind ?? "").toLowerCase();
                      const isReq = kind === "requirement";
                      return (
                        <div
                          key={i}
                          style={{
                            display: "flex",
                            gap: 10,
                            marginBottom: 8,
                            alignItems: "flex-start",
                          }}
                        >
                          <span
                            style={{
                              fontSize: 10,
                              fontWeight: 700,
                              padding: "2px 6px",
                              borderRadius: 3,
                              background: isReq ? "var(--critical-dim)" : "var(--accent-dim)",
                              color: isReq ? "var(--critical)" : "var(--accent)",
                              flexShrink: 0,
                              marginTop: 2,
                              letterSpacing: "0.05em",
                            }}
                          >
                            {isReq ? "REQUIRED" : "INFO"}
                          </span>
                          <div>
                            <div
                              style={{
                                fontWeight: 500,
                                fontSize: 13,
                                color: "var(--text-primary)",
                              }}
                            >
                              {step?.title != null ? String(step.title) : ""}
                            </div>
                            {step?.detail ? (
                              <div
                                style={{
                                  fontSize: 12,
                                  color: "var(--text-secondary)",
                                  marginTop: 2,
                                  lineHeight: 1.5,
                                }}
                              >
                                {String(step.detail)}
                              </div>
                            ) : null}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : null}

                {m.kind === "success_card" && m.adminConsoleUrl ? (
                  <a
                    href={m.adminConsoleUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-3 inline-flex items-center rounded-lg border border-emerald-400/40 bg-emerald-500/15 px-3 py-2 text-sm font-semibold text-emerald-100 hover:bg-emerald-500/25"
                  >
                    {getOpenInAdminConsoleLabel(project)}
                  </a>
                ) : null}

                {m.role === "assistant" && m.explainWhy ? (
                  <ArchitectsNote text={m.explainWhy} securityRecommendation={Boolean(m.securityRecommendation)} />
                ) : null}
              </div>
              ),
            )}
            {groupRulesPanelVisible ? <GroupRulesPanel view={groupRulesView} /> : null}
          </Fragment>
        )}

        {!formActive && !wizardActive && messages.length > 12 ? (
          <p className="text-center text-[10px] text-slate-600">Showing last 12 messages — focus on the task above when a form is active.</p>
        ) : null}

        {executionProgress && typeof executionProgress === "object" && Array.isArray(executionProgress.steps) ? (
          <div className="mr-4 rounded-xl border border-emerald-400/35 bg-emerald-500/[0.12] px-3 py-2.5 text-[13px] text-emerald-100/95">
            <p className="font-semibold">I am now running lifecycle automation</p>
            <AutomationProgressBar steps={executionProgress.steps} />
          </div>
        ) : executionProgress ? (
          <div className="mr-4 rounded-xl border border-emerald-400/35 bg-emerald-500/[0.12] px-3 py-2.5 text-[13px] font-medium text-emerald-100/95">
            {executionProgress}
          </div>
        ) : null}

        <RateLimitDisplay visible={chatHttp429Active} secondsRemaining={rateLimitRetrySeconds} />

        {refiningAgentResponse ? (
          <div className="mr-4 rounded-xl border border-amber-400/30 bg-amber-500/[0.12] px-3 py-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-amber-200/90">Refining response</p>
            <p className="mt-1 text-sm text-amber-50/95">Parsing failed once — retrying with a shorter, JSON-only prompt…</p>
          </div>
        ) : null}

        {showThinkingBanner && (
          <div className="mr-4 rounded-xl border border-white/[0.08] bg-white/[0.02] px-3 py-2.5">
            <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-500">
              {phase === "gathering" ? "Gathering" : "Thinking"}
            </p>
            <div className="mt-2 flex items-center gap-3 text-xs text-slate-400">
              <span
                className="inline-flex h-4 w-4 shrink-0 animate-spin rounded-full border-2 border-[rgb(var(--idaxis-accent-primary-rgb)/1)] border-t-transparent"
                aria-hidden
              />
              <span>
                {phase === "gathering" && oinCatalogSearchBusy
                  ? "Searching OIN catalog…"
                  : phase === "gathering"
                    ? "Working on your request…"
                    : "idwiz AI is reasoning about your tenant context…"}
              </span>
            </div>
          </div>
        )}
        <div ref={threadEndRef} />
        </div>
            </>
          }
          footerChildren={
            <>
        {tenantTouchPhase ? (
          <SswsTenantStrip
            currentContext={currentContext}
            disabled={thinking}
          />
        ) : null}

        {showSuggestedActionsChrome ? (
          <div className="space-y-2">
            {!chatDockExpandedEffective ? null : (
              <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Suggested actions</p>
            )}
            {typeof dismissSuggestedActionSurface === "function" ? (
              <button
                type="button"
                disabled={thinking}
                onClick={() => dismissSuggestedActionSurface()}
                className="w-full rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2 text-center text-[12px] font-medium text-slate-200 hover:bg-white/[0.08] disabled:cursor-not-allowed disabled:opacity-40"
              >
                {wizardActive ? "Cancel" : "Back to Security Menu"}
              </button>
            ) : null}
          </div>
        ) : null}

        <DiscoveryOptionGrid
          options={activeOptionButtons}
          disabled={actionChipsDisabled}
          canCreate={canMutateTab}
          compact={showWorkspaceTabs}
          hidden={formActive || wizardActive || showCsvExecute || oinSearchTaskLocked || !chatDockExpandedEffective}
          onSelect={(opt) => {
            applyNextStep(opt);
            queueMicrotask(() => scrollMainToBottom());
          }}
        />

        {!oinSearchTaskLocked ? (
        <div
          className={`space-y-1.5 rounded-xl border border-dashed transition-colors ${
            csvImportPickerVisible && csvDragOver
              ? "border-[rgb(var(--idaxis-accent-primary-rgb)/0.5)] bg-[rgb(var(--idaxis-accent-primary-rgb)/0.06)] p-2"
              : "border-transparent p-0"
          }`}
          {...(csvImportPickerVisible
            ? {
                onDragEnter: handleCsvDragEnter,
                onDragOver: handleCsvDragOver,
                onDragLeave: handleCsvDragLeave,
                onDrop: handleCsvDrop,
              }
            : {})}
        >
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] font-semibold uppercase tracking-widest text-slate-500">Custom input</p>
            <input
              ref={csvInputRef}
              type="file"
              accept=".csv,text/csv"
              className="hidden"
              aria-hidden
              onChange={onCsvInputChange}
            />
            {activeWorkflow === ACTIVE_WORKFLOW_CSV_IMPORT && typeof onCsvFile === "function" ? (
              <button
                type="button"
                onClick={onPickCsvClick}
                disabled={chatLocked}
                className="rounded-md border border-white/10 bg-white/[0.04] px-2 py-1 text-[10px] font-medium text-slate-300 hover:bg-white/[0.08] disabled:cursor-not-allowed disabled:opacity-40"
              >
                Choose CSV
              </button>
            ) : null}
          </div>
          {csvImportPickerVisible && csvDragOver ? (
            <p className="text-center text-[11px] text-[rgb(var(--idaxis-accent-primary-rgb)/0.9)]">Drop CSV here to import users</p>
          ) : null}
          <div className="flex gap-2">
            <textarea
              ref={inputRef}
              rows={1}
              value={input}
              onChange={typeof handleInputChange === "function" ? handleInputChange : (e) => setInput(e.target.value)}
              onInput={(e) => {
                const el = e.target;
                el.style.height = "auto";
                el.style.height = `${Math.min(120, Math.max(40, el.scrollHeight))}px`;
              }}
              onKeyDown={(e) =>
                e.key === "Enter" &&
                !e.shiftKey &&
                !dailyLimitReached &&
                !thinking &&
                !wizardActive &&
                (manualRetryPending && !input.trim() ? true : Boolean(input.trim())) &&
                (e.preventDefault(), sendMessage())
              }
              placeholder={getCommandWindowChatPlaceholder(project)}
              className={`min-h-[40px] max-h-[120px] min-w-0 flex-1 resize-none rounded-md border px-3 py-2.5 text-[13px] leading-relaxed placeholder:text-slate-600 focus:outline-none focus:ring-1 ${
                inputWakeBounce ? "idwiz-input-wake-bounce" : ""
              }`}
              style={{
                background: "var(--bg-input)",
                borderColor: "var(--border)",
                color: "var(--text-primary)",
              }}
              disabled={chatLocked}
            />
            {showActButton ? (
              <button
                type="button"
                onClick={() => {
                  if (dailyLimitReached || thinking || !input.trim() || wizardActive) return;
                  sendMessage({ executeIntent: true });
                }}
                disabled={dailyLimitReached || thinking || !input.trim() || wizardActive}
                className="min-w-[3.5rem] rounded-lg border border-[rgb(var(--idaxis-accent-primary-rgb)/0.45)] bg-[rgb(var(--idaxis-accent-primary-rgb)/0.12)] px-3 py-2 text-sm font-semibold text-[rgb(var(--idaxis-accent-primary-rgb)/1)] transition hover:bg-[rgb(var(--idaxis-accent-primary-rgb)/0.2)] disabled:cursor-not-allowed disabled:opacity-40"
              >
                Act
              </button>
            ) : null}
            <button
              type="button"
              onClick={(e) => {
                if (
                  manualRetryPending &&
                  !input.trim() &&
                  typeof forceQuotaRetryNow === "function" &&
                  e.detail >= 2
                ) {
                  e.preventDefault();
                  forceQuotaRetryNow();
                  return;
                }
                if (dailyLimitReached || thinking || (!input.trim() && !manualRetryPending) || wizardActive) {
                  return;
                }
                sendMessage();
              }}
              disabled={
                dailyLimitReached ||
                thinking ||
                (!input.trim() && !manualRetryPending) ||
                wizardActive
              }
              className={`min-w-[5.5rem] rounded-lg px-4 py-2 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-40 ${
                dailyLimitReached
                  ? "border border-rose-400/35 bg-rose-500/15 text-rose-100"
                  : manualRetryPending && !input.trim()
                    ? "border border-[rgb(var(--idaxis-accent-primary-rgb)/0.5)] bg-[rgb(var(--idaxis-accent-primary-rgb)/0.15)] text-[rgb(var(--idaxis-accent-primary-rgb)/1)]"
                    : sendReadyPulse
                      ? "idwiz-send-ready-neon border border-[rgb(var(--idaxis-accent-primary-rgb)/0.5)] bg-[rgb(var(--idaxis-accent-primary-rgb)/1)] text-[var(--idaxis-bg-primary)]"
                      : "border border-transparent bg-[rgb(var(--idaxis-accent-primary-rgb)/1)] text-[var(--idaxis-bg-primary)] shadow-[0_0_16px_rgb(var(--idaxis-accent-primary-rgb)/0.25)] hover:shadow-[0_0_22px_rgb(var(--idaxis-accent-primary-rgb)/0.35)]"
              }`}
            >
              {dailyLimitReached
                ? "Daily Limit Reached"
                : manualRetryPending && !input.trim()
                  ? "Retry Now"
                  : "Send"}
            </button>
          </div>
        </div>
        ) : null}
            </>
          }
        />
      </div>

      {oryAttestationOpen && project?.projectId ? (
        <div
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 200,
            background: "rgba(5, 8, 16, 0.72)",
            display: "grid",
            placeItems: "center",
            padding: "24px",
          }}
          role="dialog"
          aria-modal="true"
          aria-labelledby="ory-attestation-gate-title"
        >
          <div
            style={{
              width: "100%",
              maxWidth: "560px",
              maxHeight: "90vh",
              overflow: "auto",
              background: "var(--bg-surface)",
              border: "1px solid var(--border)",
              borderRadius: "10px",
              padding: "24px",
            }}
          >
            <OryAttestationGate
              projectId={project.projectId}
              onComplete={handleOryAttestationComplete}
              onCancel={() => {
                oryAttestationRetryRef.current = false;
                setOryAttestationOpen(false);
              }}
            />
          </div>
        </div>
      ) : null}
    </section>
  );
}
